import { smsSettingsService } from './sms-settings-service.js';
import { twilioService } from './twilio-service.js';
import { supabase } from './supabase.js';
import { format } from 'date-fns';

export const notificationManager = {
  // Send notification based on user preferences
  async sendNotification(userId, notificationType, data, familyId = null) {
    try {
      // For testing, we'll skip preferences check if it's explicitly a test
      if (data.isTest) {
        const testMessage = this.generateTestMessage(notificationType, data);
        const result = await twilioService.sendSMS('+14405502774', testMessage); // Replace with your test number
        return { sent: result.success, reason: result.success ? 'Test SMS sent' : result.error };
      }
      
      // Check if user has SMS enabled - need familyId and memberId
      // For now, skip SMS notifications until we have proper family context
      console.log('SMS notifications temporarily disabled - need family context');
      return { sent: false, reason: 'SMS service needs family context' };
      
      if (!preferences.is_enabled || !preferences.phone_number) {
        return { sent: false, reason: 'SMS not enabled' };
      }

      const deliveryMethod = preferences.preferences[notificationType];
      
      if (!deliveryMethod || deliveryMethod === 'off') {
        return { sent: false, reason: 'Notification type disabled' };
      }

      if (deliveryMethod === 'individual') {
        // Send immediately
        return await this.sendIndividualNotification(userId, notificationType, data, preferences.phone_number);
      } else if (deliveryMethod === 'digest') {
        // Queue for daily digest
        await this.queueForDigest(userId, notificationType, data);
        return { sent: false, queued: true, reason: 'Queued for digest' };
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      return { sent: false, error: error.message };
    }
  },

  // Send individual notification
  async sendIndividualNotification(userId, notificationType, data, phoneNumber) {
    let message;
    
    switch (notificationType) {
      case 'familyTimeInvites':
        message = twilioService.templates.familyTimeInvite(
          data.inviterName,
          data.activity,
          data.time
        );
        break;
        
      case 'familyActivityVoting':
        message = twilioService.templates.familyActivityVoting(data.activityTitle);
        break;
        
      case 'confirmedFamilyPlans':
        message = twilioService.templates.confirmedFamilyPlan(
          data.activity,
          data.datetime
        );
        break;
        
      case 'newIdeas':
        message = twilioService.templates.newIdea(data.title, data.category);
        break;
        
      default:
        return { sent: false, reason: 'Unknown notification type' };
    }

    const result = await twilioService.sendSMS(
      phoneNumber,
      message,
      userId,
      notificationType
    );

    return { sent: result.success, ...result };
  },

  // Queue notification for digest - temporarily disabled
  async queueForDigest(userId, notificationType, data) {
    console.log('Digest notifications temporarily disabled - need notification queue table');
    return { queued: false, reason: 'Digest system not implemented' };
  },

  // Check and send planning reminders
  async checkPlanningReminders() {
    try {
      const { data: users } = await supabase
        .from('family_members')
        .select('*');

      for (const user of users) {
        // Skip SMS reminders for now - need family context
        console.log('SMS reminders temporarily disabled - need family context');
        continue;
        
        if (!preferences.is_enabled || !preferences.phone_number) continue;

        const now = new Date();
        const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

        // Check daily planning reminder
        if (preferences.preferences.dailyPlanningReminder.enabled &&
            preferences.preferences.dailyPlanningReminder.time === currentTime) {
          
          // Check if user has plans for today
          const today = format(now, 'yyyy-MM-dd');
          const { data: todaysPlan } = await supabase
            .from('plans')
            .select('*')
            .eq('kid_id', user.id)
            .eq('date', today)
            .single();

          if (!todaysPlan || !todaysPlan.activities?.length) {
            await twilioService.sendSMS(
              preferences.phone_number,
              twilioService.templates.dailyPlanningReminder(user.name),
              user.id,
              'dailyPlanningReminder'
            );
          }
        }

        // Check daily check-in reminder
        if (preferences.preferences.dailyCheckinReminder.enabled &&
            preferences.preferences.dailyCheckinReminder.time === currentTime) {
          
          // Check if user has uncompleted tasks
          const today = format(now, 'yyyy-MM-dd');
          const { data: todaysPlan } = await supabase
            .from('plans')
            .select('*')
            .eq('kid_id', user.id)
            .eq('date', today)
            .single();

          const hasUncompletedTasks = todaysPlan?.activities?.some(
            activity => activity.status !== 'completed'
          );

          if (hasUncompletedTasks) {
            await twilioService.sendSMS(
              preferences.phone_number,
              twilioService.templates.dailyCheckinReminder(user.name),
              user.id,
              'dailyCheckinReminder'
            );
          }
        }
      }
    } catch (error) {
      console.error('Error checking planning reminders:', error);
    }
  },

  // Send daily digests
  async sendDailyDigests() {
    try {
      const currentTime = new Date().toTimeString().slice(0, 5); // HH:MM format
      // Daily digests temporarily disabled
      console.log('Daily digests temporarily disabled - need notification queue table');
      return;

      for (const user of usersForDigest) {
        // Get all digest items for this user
        const { data: digestItems } = await supabase
          .from('notification_queue')
          .select('*')
          .eq('user_id', user.user_id)
          .eq('notification_type', 'daily_digest_item')
          .eq('status', 'pending');

        if (!digestItems || digestItems.length === 0) continue;

        // Compile digest data
        const digestData = {
          ideas: 0,
          invites: 0,
          activities: 0,
          confirmed: 0
        };

        digestItems.forEach(item => {
          switch (item.payload.type) {
            case 'newIdeas':
              digestData.ideas++;
              break;
            case 'familyTimeInvites':
              digestData.invites++;
              break;
            case 'familyActivityVoting':
              digestData.activities++;
              break;
            case 'confirmedFamilyPlans':
              digestData.confirmed++;
              break;
          }
        });

        // Send digest if there are items
        if (Object.values(digestData).some(count => count > 0)) {
          const message = twilioService.templates.dailyDigest(digestData);
          
          await twilioService.sendSMS(
            user.phone_number,
            message,
            user.user_id,
            'daily_digest'
          );

          // Mark all digest items as sent
          const itemIds = digestItems.map(item => item.id);
          await supabase
            .from('notification_queue')
            .update({ status: 'sent' })
            .in('id', itemIds);
        }
      }
    } catch (error) {
      console.error('Error sending daily digests:', error);
    }
  },

  // 1:1 Invite notification
  async notifyFamilyTimeInvite(invite, inviterName, inviteeName) {
    await this.sendNotification(invite.invitee, 'familyTimeInvites', {
      inviterName,
      activity: invite.activity_idea,
      time: invite.preferred_time || 'at your convenience'
    });
  },

  // New Family Event notification
  async notifyNewFamilyActivity(activity, familyMembers) {
    for (const member of familyMembers) {
      if (member.id !== activity.createdBy) {
        await this.sendNotification(member.id, 'familyActivityVoting', {
          activityTitle: activity.title
        });
      }
    }
  },

  // Confirmed Family Plan notification
  async notifyConfirmedPlan(event, familyMembers) {
    const datetime = `${format(new Date(event.date), 'EEEE MMM d')} ${event.time || ''}`;
    
    for (const member of familyMembers) {
      await this.sendNotification(member.id, 'confirmedFamilyPlans', {
        activity: event.title,
        datetime
      });
    }
  },

  // New Idea notification
  async notifyNewIdea(idea, targetMembers) {
    for (const memberId of targetMembers) {
      await this.sendNotification(memberId, 'newIdeas', {
        title: idea.title,
        category: idea.category || 'recommendation'
      });
    }
  },

  // Generate test message for SMS testing
  generateTestMessage(notificationType, data) {
    switch (notificationType) {
      case 'familyTimeInvite':
        return `[TEST] 🌟 ${data.inviterName} wants to do ${data.activity} on ${data.time}. Reply Y to accept or N to decline.`;
      case 'familyActivityVoting':
        return `[TEST] 🗳️ New family activity proposal: "${data.activityTitle}". Cast your vote in the app!`;
      case 'confirmedFamilyPlan':
        return `[TEST] ✅ Confirmed: ${data.activity} on ${data.datetime}. Looking forward to it!`;
      case 'newIdea':
        return `[TEST] 💡 New ${data.category} idea shared: "${data.title}". Check it out in the app!`;
      default:
        return `[TEST] Notification test for ${notificationType}`;
    }
  }
};